import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { CatalogItem, InventoryBalance, ItemCategory } from "../api/types";
import { groupInventoryByItem } from "../utils/inventoryProducts";
import { matchesInventorySearch } from "../utils/inventorySearch";
import { topCategories } from "../utils/topCategoryChips";
import { isVesselItemCategory } from "../utils/vesselItemCategory";
import { AdjustInventoryStockModal } from "./AdjustInventoryStockModal";
import { InventoryCardList } from "./inventory/InventoryCardList";
import { InventoryItemCard, type InventoryCardTarget } from "./inventory/InventoryItemCard";
import { InventorySearchCard } from "./InventorySearchCard";
import { ItemImageEnlargeModal } from "./ItemImageEnlargeModal";
import { NavIcon, type NavIconName } from "./nav/NavIcon";
import { Chip } from "./ui/Chip";

/** The three views that replace the product list (G-132). */
export type InventoryView = "bouquets" | "low" | "order";

export type InventoryViewChip = {
  id: InventoryView;
  label: string;
  icon: NavIconName;
  /** undefined while it is still loading. */
  count?: number;
};

export type InventoryPanelProps = {
  /**
   * G-132: the chips that open a view in place of the list, in the order they
   * show. The host decides which exist (a role, a chosen shop, something to
   * say) and what each counts; the panel only lays them out and hides its own
   * filters while one is open.
   */
  viewChips?: InventoryViewChip[];
  activeView?: InventoryView | null;
  onActiveViewChange?: (view: InventoryView | null) => void;
  /** The content of the open view; it gets the search text, so a view can filter by it. */
  renderView?: (view: InventoryView, searchQuery: string) => ReactNode;
  /**
   * G-94 — what to OFFER when a search or a scan matches nothing, anywhere.
   *
   * On the staff inventory page an unknown barcode is usually a product the
   * shop has just started carrying, so the offer there is to create it. It
   * receives the trimmed query so the host can carry it into whatever it opens.
   * It is a SUGGESTION rendered next to the "nothing matches" line — nothing is
   * created until the person taps it.
   */
  renderNoMatchAction?: (query: string) => ReactNode;
  /**
   * G-94 S2 — raise "receive this item" to whoever owns the shop and its zones.
   * The panel knows what is on the shelf; only its host knows which shop is in
   * view, and receiving needs both. Absent, the item card does not offer it.
   */
  onReceive?: (preset: { itemId: string; itemName: string }) => void;
  /** Bumped by the host after a write of its own, so the balances re-read. */
  reloadSignal?: number;
  /**
   * G-94 S4 — hand the host the rows this panel just fetched.
   *
   * The shop tools need them (transfer and waste can only offer what the shop
   * actually holds) and the panel has already asked the server for exactly that
   * list. A second request for the same rows would be a second answer that can
   * disagree with the first.
   */
  onRowsChange?: (rows: InventoryBalance[]) => void;
  /** Items on the low-stock list: their total in the product row turns to the warning colour. */
  lowItemIds?: ReadonlySet<string>;
};

/**
 * G-89 — an item with no stock is still worth showing.
 *
 * Every product row is built from balance rows, so an item the catalogue knows
 * but this view holds none of was invisible: searching its name gave "Nothing
 * matches", which reads as "no such item". During the first whole-shop
 * stocktake that is exactly backwards — the items you most need to find are the
 * ones not yet counted in. They appear below the stock, marked zero, capped and
 * only once two characters are typed (one letter matches most of the catalogue
 * and would swamp the shelves).
 */
const CATALOGUE_MIN_CHARS = 2;
const CATALOGUE_MAX_RESULTS = 12;
const CATALOGUE_SETTLE_MS = 350;
const AUTO_RELOAD_MIN_MS = 30_000;

/** A typed minimum size in cm; blank, negative or unreadable means no minimum. */
function parseMinCm(input: string): number | undefined {
  const t = input.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function hasPositiveQtyOnHand(row: InventoryBalance): boolean {
  const q = Number.parseFloat(row.qtyOnHand);
  return Number.isFinite(q) && q > 0;
}

export function InventoryPanel({
  viewChips = [],
  activeView = null,
  onActiveViewChange,
  renderView,
  renderNoMatchAction,
  onReceive,
  reloadSignal = 0,
  onRowsChange,
  lowItemIds,
}: InventoryPanelProps) {
  const [searchParams] = useSearchParams();
  const fetchLocationId = searchParams.get("locationId") ?? "";
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilterId, setCategoryFilterId] = useState("");
  const [minDiameterInput, setMinDiameterInput] = useState("");
  const [minDepthInput, setMinDepthInput] = useState("");
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [categoryLoadError, setCategoryLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<InventoryBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [adjustRow, setAdjustRow] = useState<InventoryBalance | null>(null);
  /**
   * G-94 S3 — the item card belongs to the PANEL, not to the list: one card,
   * opened from any row, with the same actions.
   */
  const [openTarget, setOpenTarget] = useState<InventoryCardTarget | null>(null);
  const [imagePreview, setImagePreview] = useState<{
    itemId: string;
    itemName: string;
    imageKey: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listCategories()
      .then((list) => {
        if (!cancelled) {
          setCategories([...list].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })));
          setCategoryLoadError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCategoryLoadError(err instanceof Error ? err.message : "Could not load categories");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryFilterId),
    [categories, categoryFilterId],
  );
  const vesselActive = selectedCategory != null && isVesselItemCategory(selectedCategory.code);

  const vesselMinDiameterCm = useMemo(
    () => (vesselActive ? parseMinCm(minDiameterInput) : undefined),
    [vesselActive, minDiameterInput],
  );
  const vesselMinDepthCm = useMemo(
    () => (vesselActive ? parseMinCm(minDepthInput) : undefined),
    [vesselActive, minDepthInput],
  );

  /**
   * G-132: the category filter is client-side — the page fetches every row so
   * the chips can show real counts. The vessel size filter is the exception: it
   * runs on the server and needs the category with it.
   */
  const serverCategoryId =
    vesselMinDiameterCm != null || vesselMinDepthCm != null ? categoryFilterId : "";

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getBalance({
          locationId: fetchLocationId || undefined,
          categoryId: serverCategoryId || undefined,
          minDiameterCm: vesselMinDiameterCm,
          minDepthCm: vesselMinDepthCm,
        });
        if (ac.signal.aborted) return;
        setRows(data);
        onRowsChange?.(data);
      } catch (err: unknown) {
        if (ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Could not load inventory");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [fetchLocationId, reloadTick, reloadSignal, serverCategoryId, vesselMinDiameterCm, vesselMinDepthCm]);

  /**
   * G-132: no Refresh button — coming back to the tab or window re-reads the
   * balances, at most once every 30 s (a focus event also fires when a modal
   * closes). Writes already bump `reloadSignal`.
   */
  const lastAutoReloadAt = useRef(Date.now());
  useEffect(() => {
    const maybeReload = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastAutoReloadAt.current < AUTO_RELOAD_MIN_MS) return;
      lastAutoReloadAt.current = Date.now();
      setReloadTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", maybeReload);
    window.addEventListener("focus", maybeReload);
    return () => {
      document.removeEventListener("visibilitychange", maybeReload);
      window.removeEventListener("focus", maybeReload);
    };
  }, []);

  const searching = Boolean(searchQuery.trim());

  /**
   * One row per product, folded from ALL the rows, then narrowed: a product
   * matches a search when any of its placements does (name, colour, shop, zone,
   * barcode), and keeps every placement either way — a search for "fridge"
   * should not shrink a product's total to what the fridge holds.
   */
  const allProducts = useMemo(() => groupInventoryByItem(rows), [rows]);
  const searchedProducts = useMemo(
    () =>
      searching
        ? allProducts.filter((p) => p.placements.some((r) => matchesInventorySearch(r, searchQuery, { includeColour: true })))
        : allProducts,
    [allProducts, searching, searchQuery],
  );
  const products = useMemo(
    () =>
      categoryFilterId
        ? searchedProducts.filter((p) => p.item.category.id === categoryFilterId)
        : searchedProducts,
    [searchedProducts, categoryFilterId],
  );

  // Chip counts follow the search but not the category choice, so picking one
  // category does not make the others vanish from the row.
  const chips = useMemo(() => topCategories(searchedProducts, 4), [searchedProducts]);
  const selectedIsChip = chips.some((c) => c.id === categoryFilterId);

  const byShop = useMemo(() => new Set(rows.map((r) => r.location.id)).size > 1, [rows]);

  const chooseCategory = (id: string) => {
    setCategoryFilterId(id);
    const next = categories.find((c) => c.id === id);
    if (!next || !isVesselItemCategory(next.code)) {
      setMinDiameterInput("");
      setMinDepthInput("");
    }
  };

  /** Catalogue lookup for the "not in stock here" list. Silent on failure. */
  const [catalogueMatches, setCatalogueMatches] = useState<CatalogItem[]>([]);
  useEffect(() => {
    const typed = searchQuery.trim();
    if (typed.length < CATALOGUE_MIN_CHARS) {
      setCatalogueMatches([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void api
        .searchItems(typed, CATALOGUE_MAX_RESULTS)
        .then((items) => {
          if (!cancelled) setCatalogueMatches(items);
        })
        .catch(() => {
          if (!cancelled) setCatalogueMatches([]);
        });
    }, CATALOGUE_SETTLE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  const notStockedHere = useMemo(() => {
    // The vessel size filters narrow the BALANCE request server-side but mean
    // nothing to the catalogue search, so with one active `rows` no longer
    // says what the shop holds: a 12 cm vase with six on hand is simply
    // absent, and calling it "not in stock" would be a lie the page tells
    // about stock it is itself hiding. The zero list stands down instead.
    if (vesselMinDiameterCm != null || vesselMinDepthCm != null) return [];
    const stocked = new Set(allProducts.map((p) => p.item.id));
    return catalogueMatches.filter(
      (item) =>
        !stocked.has(item.id) &&
        (!categoryFilterId || item.category.id === categoryFilterId),
    );
  }, [catalogueMatches, allProducts, categoryFilterId, vesselMinDiameterCm, vesselMinDepthCm]);

  const hasSearchNoMatches = searching && products.length === 0 && rows.length > 0;
  const hasZeroQtyOnly = rows.length > 0 && !rows.some(hasPositiveQtyOnHand);

  return (
    <section className="inventory-panel page-stack" aria-label="Inventory balances">
      <InventorySearchCard
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        showVesselSize={vesselActive}
        minDiameterInput={minDiameterInput}
        minDepthInput={minDepthInput}
        onMinDiameterInputChange={setMinDiameterInput}
        onMinDepthInputChange={setMinDepthInput}
      />

      {categoryLoadError ? <p className="field-hint inv-category-error">{categoryLoadError}</p> : null}

      {/* G-132: the quick filters. They wrap rather than scroll sideways. The
          view chips come first (and swap their glyph for a close icon while
          open); the category chips are single-select, and "More" reaches the
          rest. Category chips hide while a view is open — they filter products. */}
      {viewChips.length > 0 || chips.length > 0 || selectedCategory ? (
        <div className="inv-filter-chips" role="group" aria-label="Filters">
          {viewChips.map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              count={chip.count}
              icon={activeView === chip.id ? "close" : chip.icon}
              selected={activeView === chip.id}
              onClick={() => onActiveViewChange?.(activeView === chip.id ? null : chip.id)}
            />
          ))}
          {!activeView ? chips.map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              count={chip.count}
              selected={categoryFilterId === chip.id}
              onClick={() => chooseCategory(categoryFilterId === chip.id ? "" : chip.id)}
            />
          )) : null}
          {!activeView && selectedCategory && !selectedIsChip ? (
            <Chip
              label={selectedCategory.label}
              icon="close"
              selected
              onClick={() => chooseCategory("")}
            />
          ) : null}
          {!activeView ? <span className="inv-more">
            <select
              className="kit-chip inv-more-select"
              aria-label="More categories"
              value=""
              onChange={(e) => chooseCategory(e.target.value)}
            >
              <option value="">More</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
            <NavIcon name="chevronDown" size={14} />
          </span> : null}
        </div>
      ) : null}

      {activeView ? renderView?.(activeView, searchQuery) : null}

      {!activeView && error ? (
        <div className="card">
          <div className="alert error">{error}</div>
        </div>
      ) : null}

      {!activeView && hasSearchNoMatches && notStockedHere.length === 0 ? (
        <div className="card inventory-no-match">
          <p className="muted">
            Nothing matches &ldquo;{searchQuery.trim()}&rdquo;. Try another term or clear the
            search.
          </p>
          {renderNoMatchAction?.(searchQuery.trim())}
        </div>
      ) : null}

      {!activeView && !error && rows.length === 0 && !loading ? (
        <div className="card empty-state">No balances in this view yet.</div>
      ) : null}

      {!activeView && !error && hasZeroQtyOnly && !loading && !searching ? (
        <div className="card empty-state">No on-hand quantity in this view.</div>
      ) : null}

      {!activeView && !error && (products.length > 0 || notStockedHere.length > 0) ? (
        <InventoryCardList
          products={products}
          notStockedHere={notStockedHere}
          byShop={byShop}
          lowItemIds={lowItemIds}
          searching={searching}
          onThumbnailClick={(item) =>
            setImagePreview({ itemId: item.id, itemName: item.name, imageKey: item.imageKey ?? null })
          }
          onOpenItem={setOpenTarget}
        />
      ) : null}

      {!activeView && !error && products.length === 0 && notStockedHere.length === 0 && categoryFilterId && !hasSearchNoMatches && rows.length > 0 && !hasZeroQtyOnly ? (
        <div className="card empty-state">No stock in this category for this view.</div>
      ) : null}

      {imagePreview ? (
        <ItemImageEnlargeModal
          itemId={imagePreview.itemId}
          itemName={imagePreview.itemName}
          imageKey={imagePreview.imageKey}
          onClose={() => setImagePreview(null)}
        />
      ) : null}

      {openTarget ? (
        <InventoryItemCard
          target={openTarget}
          shopId={fetchLocationId || undefined}
          shopName={
            fetchLocationId
              ? rows.find((r) => r.location.id === fetchLocationId)?.location.name
              : undefined
          }
          onReceive={onReceive}
          onClose={() => setOpenTarget(null)}
          onAdjust={(row) => {
            setOpenTarget(null);
            setAdjustRow(row);
          }}
        />
      ) : null}

      {adjustRow ? (
        <AdjustInventoryStockModal
          row={adjustRow}
          onClose={() => setAdjustRow(null)}
          onSaved={() => setReloadTick((t) => t + 1)}
        />
      ) : null}

      {!activeView && loading && rows.length === 0 && !error ? (
        <div className="card muted inv-loading">Loading…</div>
      ) : null}
    </section>
  );
}
